import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, Tag, Gift, HandHeart, MoreHorizontal } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { toast } from "../hooks/use-toast";

const itemTypes = [
  {
    key: "seller",
    tabLabel: "For Sale",
    heading: "Items For Sale",
    description: "Browse items sellers currently have listed.",
    icon: Tag,
  },
  {
    key: "donator",
    tabLabel: "Donations",
    heading: "Donations",
    description: "Free items being donated by the community.",
    icon: Gift,
  },
  {
    key: "giveaway",
    tabLabel: "Give Away",
    heading: "Give Away Items",
    description: "Items being given away at no cost.",
    icon: HandHeart,
  },

];

const knownItemTypes = new Set(
  itemTypes.filter((type) => type.key !== "other").map((type) => type.key)
);

const ItemTypeSections = ({ items, slug }) => {
  const { toggleCartItem, isInCart } = useCart();

  const normalizedSlug = useMemo(
    () => slug?.replace(/-/g, " ")?.trim()?.toLowerCase(),
    [slug]
  );

  const normalize = useCallback(
    (value) => String(value || "").trim().toLowerCase(),
    []
  );

  const [activeType, setActiveType] = useState(itemTypes[0].key);

  const [sortType, setSortType] = useState({
    seller: "latest",
    donator: "latest",
    giveaway: "latest",
    other: "latest",
  });

  const sortItems = useCallback((list, type) => {
    const copy = [...list];

    if (type === "price") {
      return copy.sort(
        (a, b) => Number(a.amount || 0) - Number(b.amount || 0)
      );
    }

    return copy.sort(
      (a, b) => Number(b.itemId || 0) - Number(a.itemId || 0)
    );
  }, []);

  const categorizedData = useMemo(() => {
    const result = { seller: [], donator: [], giveaway: [], other: [] };

    items.forEach((item) => {
      if (normalize(item.categoryName) !== normalizedSlug) {
        return;
      }

      const normalizedType = normalize(item.type);
      const bucket = knownItemTypes.has(normalizedType) ? normalizedType : "other";

      result[bucket].push(item);
    });

    return result;
  }, [items, normalizedSlug, normalize]);

  const sortedData = useMemo(() => {
    const result = {};

    itemTypes.forEach((type) => {
      result[type.key] = sortItems(categorizedData[type.key] || [], sortType[type.key]);
    });

    return result;
  }, [categorizedData, sortType, sortItems]);

  const timeAgo = (timestamp) => {
    if (!timestamp) return "Unknown";

    const postedTime = new Date(timestamp).getTime();
    if (Number.isNaN(postedTime)) return "Invalid date";

    const diffInSeconds = Math.floor((Date.now() - postedTime) / 1000);
    if (diffInSeconds < 0) return "Just now";
    if (diffInSeconds < 60) return `${diffInSeconds}s ago`;

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30) return `${diffInDays}d ago`;

    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) return `${diffInMonths}mo ago`;

    return `${Math.floor(diffInMonths / 12)}y ago`;
  };

  const getItemStatus = (item) => {
    if (item.status?.toLowerCase() === "sold") return "Sold";

    const posted = new Date(item.timestamp).getTime();
    const period = Number(item.availPeriod);

    if (
      Number.isFinite(posted) &&
      Number.isFinite(period) &&
      Date.now() >= posted + period * 60 * 60 * 1000
    ) {
      return "Expired";
    }

    return timeAgo(item.timestamp);
  };

  const toggleCart = (event, item) => {
    event.preventDefault();
    event.stopPropagation();

    const added = toggleCartItem(item);
    toast({
      title: added ? "Added to cart" : "Removed from cart",
      description: item.itemName,
      duration: 2500,
    });
  };

  const activeConfig = itemTypes.find((type) => type.key === activeType);
  const activeFiltered = categorizedData[activeType];
  const activeSorted = sortedData[activeType];

  return (
    <div className="space-y-6">
      {/*<div>
        <h2 className="font-heading text-2xl font-bold text-foreground md:text-3xl">
          Browse by <span className="text-primary">Item Type</span>
        </h2>
        <p className="mt-1.5 text-sm text-muted-foreground">
          Find exactly what you need — or post what you no longer need.
        </p>
      </div>*/}

      {/* ================= TAB BAR ================= */}
      <div className="flex w-full justify-start">
        <div
          role="tablist"
          aria-label="Item type"
          className="flex w-full items-center gap-0.5 rounded-2xl border border-border/70 bg-secondary/40 p-1 shadow-sm sm:w-fit sm:gap-1 sm:p-1.5"
        >
          {itemTypes.map((type) => {
            const Icon = type.icon;
            const count = categorizedData[type.key].length;
            const active = activeType === type.key;

            return (
              <button
                key={type.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setActiveType(type.key)}
                className={`group relative flex min-w-0 flex-1 items-center justify-center gap-1 rounded-xl px-1.5 pb-2 pt-3.5 text-[10px] font-semibold whitespace-nowrap outline-none transition-all duration-200 focus-visible:ring-2 focus-visible:ring-primary/40 sm:flex-none sm:gap-2.5 sm:px-3.5 sm:pb-2.5 sm:pt-4 sm:text-sm md:px-4 ${
                  active
                    ? "bg-card text-primary shadow-sm ring-1 ring-border/70"
                    : "text-muted-foreground hover:bg-card/70 hover:text-foreground"
                }`}
              >
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md transition-colors sm:h-7 sm:w-7 sm:rounded-lg ${
                    active
                      ? "bg-primary/10 text-primary"
                      : "bg-background/70 text-muted-foreground group-hover:text-foreground"
                  }`}
                >
                  <Icon className="h-3 w-3 sm:h-4 sm:w-4" />
                </span>

                <span className="text-[12px]">{type.tabLabel}</span>

                <span
                  className={`absolute left-6 top-0 z-10 min-w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border px-1 text-center text-[9px] font-bold leading-4 shadow-sm transition-colors sm:min-w-5 sm:px-1.5 sm:text-[10px] ${
                    active
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border bg-card text-muted-foreground"
                  }`}
                >
                  {count}
                </span>

                {active && (
                  <span className="absolute inset-x-2 -bottom-1 h-0.5 rounded-full bg-primary sm:inset-x-4" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ================= ACTIVE SECTION ================= */}
      <div className="space-y-4 rounded-lg border border-border bg-card p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-xl font-bold text-foreground">{activeConfig.heading}</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {activeFiltered.length} {activeFiltered.length === 1 ? "item" : "items"}
            </p>
          </div>

          {activeFiltered.length > 1 && (
            <select
              value={sortType[activeType]}
              onChange={(event) =>
                setSortType((previous) => ({
                  ...previous,
                  [activeType]: event.target.value,
                }))
              }
              className="rounded-md border border-input bg-background px-3 py-2 text-xs text-foreground"
            >
              <option value="latest">Latest</option>
              <option value="price">Lowest price</option>
            </select>
          )}
        </div>

        <p className="text-sm text-muted-foreground">{activeConfig.description}</p>

        {activeFiltered.length > 0 ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {activeSorted.map((item) => {
              const inCart = isInCart(item.itemId);

              return (
                <Link key={item.itemId} to={`/item/${item.itemId}`}>
                  <div className="group relative overflow-hidden rounded-lg border border-border bg-card transition-all hover:border-primary/40 hover:shadow-[var(--glow-primary)]">
                    <div className="relative h-48 overflow-hidden bg-secondary">
                      <button
                        type="button"
                        aria-label={inCart ? "Remove from cart" : "Add to cart"}
                        onClick={(event) => toggleCart(event, item)}
                        className={`absolute right-3 top-3 z-10 rounded-lg border px-3 py-2 backdrop-blur transition-colors ${
                          inCart
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-border bg-black/70 text-yellow-400"
                        }`}
                      >
                        <ShoppingCart className="h-4 w-4" />
                      </button>

                      {item.imageUrl && (
                        <img
                          src={item.imageUrl}
                          alt={item.itemName}
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                          loading="lazy"
                        />
                      )}
                    </div>

                    <div className="relative -mt-6 px-5 pb-5">
                      <div className="absolute bottom-3 right-3 z-10 rounded-sm border border-border px-3 py-1.5 text-xs font-medium text-yellow-400 backdrop-blur">
                        {getItemStatus(item)}
                      </div>

                      <h3 className="font-heading truncate text-lg font-semibold text-primary">
                        {item.itemName}
                      </h3>
                      <p className="mt-1 truncate text-sm text-muted-foreground">
                        {item.description}
                      </p>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {item.currency} {Number(item.amount || 0).toLocaleString()}
                      </p>
                      {(item.locCity || item.locCountry) && (
                        <p className="mt-1 pr-20 text-xs text-muted-foreground">
                          {[item.locCity, item.locCountry].filter(Boolean).join(", ")}
                        </p>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-start gap-2">
            <p className="text-sm text-muted-foreground">
              No {activeConfig.tabLabel.toLowerCase()} items match the current filters.
            </p>

            <Link
              to="/post"
              state={{ category: slug, type: activeType }}
              className="rounded-sm bg-primary px-3 py-2 text-sm text-primary-foreground"
            >
              Add Item
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default ItemTypeSections;
